<template>
  <BaseDialog
    v-model="dialogOpen"
    :title="costsOnly ? 'Cost per Platform' : 'Review Settings'"
    title-icon="mdi-cog"
    :max-width="450"
    save-text="Save"
    :save-loading="saving"
    :save-disabled="!hasChanges"
    @save="save"
  >
    <!-- Cooldown setting (hidden in costs-only mode) -->
    <v-text-field
      v-if="!costsOnly"
      v-model.number="form.cooldown"
      label="Cooldown Days"
      type="number"
      min="1"
      max="90"
      density="compact"
      variant="outlined"
      hint="Days before a review can be shown again"
      persistent-hint
      class="mb-4"
    />

    <!-- Cost per review per platform -->
    <div v-if="Object.keys(form.costs).length > 0">
      <div class="text-caption text-medium-emphasis mb-2">Cost per Review (AED)</div>
      <v-row density="compact">
        <v-col v-for="platform in sortedPlatforms" :key="platform" cols="6">
          <v-text-field
            v-model.number="form.costs[platform]"
            :label="platform"
            type="number"
            step="0.01"
            min="0"
            density="compact"
            variant="outlined"
            hide-details
            suffix="AED"
          />
        </v-col>
      </v-row>
    </div>
  </BaseDialog>
</template>

<script setup>
/**
 * ReviewSettingsDialog — edits cooldown_days and cost_per_review:<Platform>
 * entries stored in the review_config key/value table.
 *
 * Usage:
 *   <ReviewSettingsDialog v-model="dialogOpen" @saved="onSaved" />
 *
 * On open it fetches the current config; on save it PUTs all values back.
 */
import { ref, computed, watch } from "vue";
import api from "@/utils/api.js";
import BaseDialog from "@/components/BaseDialog.vue";

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  costsOnly: { type: Boolean, default: false },
});
const emit = defineEmits(["update:modelValue", "saved"]);

// v-model proxy
const dialogOpen = computed({
  get: () => props.modelValue,
  set: (v) => emit("update:modelValue", v),
});

// Form state
const form = ref({ cooldown: 15, costs: {} });
// Snapshot of original values for dirty-checking
const original = ref({ cooldown: 15, costs: {} });
const saving = ref(false);
const loading = ref(false);

// Sorted platform list for consistent rendering
const sortedPlatforms = computed(() => Object.keys(form.value.costs).sort());

// Dirty check: compare current form to original snapshot
const hasChanges = computed(() => {
  if (form.value.cooldown !== original.value.cooldown) return true;
  for (const [platform, cost] of Object.entries(form.value.costs)) {
    if (String(cost) !== String(original.value.costs[platform] || 0)) return true;
  }
  return false;
});

// Load config from backend when dialog opens
watch(dialogOpen, async (open) => {
  if (!open) return;
  loading.value = true;
  try {
    const res = await api.marketingApi.get("/api/marketing/reviews/admin/config");
    const data = res.data || {};
    // Parse cooldown
    const cooldown = parseInt(data.cooldown_days) || 15;
    // Parse cost_per_review:* entries
    const costs = {};
    for (const [key, value] of Object.entries(data)) {
      if (key.startsWith("cost_per_review:")) {
        const platform = key.replace("cost_per_review:", "");
        costs[platform] = parseFloat(value) || 0;
      }
    }
    form.value = { cooldown, costs };
    original.value = { cooldown, costs: { ...costs } };
  } catch (err) {
    console.error("Failed to load review config:", err);
  } finally {
    loading.value = false;
  }
});

// Save all settings in one PUT
async function save() {
  saving.value = true;
  try {
    const body = { cooldown_days: form.value.cooldown };
    for (const [platform, cost] of Object.entries(form.value.costs)) {
      body[`cost_per_review:${platform}`] = cost || 0;
    }
    await api.marketingApi.put("/api/marketing/reviews/admin/config", body);
    // Update snapshot so hasChanges becomes false
    original.value = { cooldown: form.value.cooldown, costs: { ...form.value.costs } };
    emit("saved");
    dialogOpen.value = false;
  } catch (err) {
    console.error("Failed to save review config:", err);
  } finally {
    saving.value = false;
  }
}
</script>
